/**
 * Idea-box history — the pure state behind useThemeHistory (both apps) and the
 * Einschulung landing script (ui/snugtale/public/einschulung.js keeps a
 * verbatim port; change both together).
 *
 * This is a browsable list, not an editor undo stack (decided 2026-08-29):
 * a new idea or typed text is INSERTED after the current position, so going
 * back to an earlier idea and asking for another one never throws away the
 * ideas you had already seen. Undo/redo just walk back and forth.
 */
export type Source = 'user' | 'pill' | 'soften';

export interface Entry {
  value: string;
  source: Source;
}

export interface HistoryState {
  entries: Entry[];
  cursor: number;
}

export interface HistoryView {
  value: string;
  canUndo: boolean;
  canRedo: boolean;
  position: number;
  total: number;
  isDirty: boolean;
}

export function initialState(initial: string = ''): HistoryState {
  return { entries: [{ value: initial, source: 'user' }], cursor: 0 };
}

// The first entry is the implicit "blank" state. Don't count it as something to undo to —
// the user thinks of an empty box as "no history," not as a previous step.
export function isInitialEmpty(state: HistoryState): boolean {
  return state.entries[0].source === 'user' && state.entries[0].value === '';
}

function insertAfterCursor(state: HistoryState, entry: Entry): HistoryState {
  const entries = [
    ...state.entries.slice(0, state.cursor + 1),
    entry,
    ...state.entries.slice(state.cursor + 1),
  ];
  return { entries, cursor: state.cursor + 1 };
}

/** Typing: edits a typed head in place; after an idea it becomes a new entry (inserted, nothing dropped). */
export function setUserValue(state: HistoryState, value: string): HistoryState {
  const head = state.entries[state.cursor];
  if (head.source === 'user') {
    const entries = state.entries.slice();
    entries[state.cursor] = { value, source: 'user' };
    return { entries, cursor: state.cursor };
  }
  return insertAfterCursor(state, { value, source: 'user' });
}

/** An "inspire me" idea / picker card: inserted after the current position. No-op when it is already the head. */
export function applyPill(state: HistoryState, value: string): HistoryState {
  if (state.entries[state.cursor].value === value) return state;
  return insertAfterCursor(state, { value, source: 'pill' });
}

/** The content screener's softened rewrite — same placement rule as an idea. */
export function applySoften(state: HistoryState, value: string): HistoryState {
  if (state.entries[state.cursor].value === value) return state;
  return insertAfterCursor(state, { value, source: 'soften' });
}

export function undo(state: HistoryState): HistoryState {
  const minCursor = isInitialEmpty(state) ? 1 : 0;
  return state.cursor > minCursor ? { ...state, cursor: state.cursor - 1 } : state;
}

export function redo(state: HistoryState): HistoryState {
  return state.cursor < state.entries.length - 1 ? { ...state, cursor: state.cursor + 1 } : state;
}

export function view(state: HistoryState): HistoryView {
  const head = state.entries[state.cursor];
  const skipFirst = isInitialEmpty(state);
  return {
    value: head.value,
    canUndo: state.cursor > (skipFirst ? 1 : 0),
    canRedo: state.cursor < state.entries.length - 1,
    position: skipFirst ? state.cursor : state.cursor + 1,
    total: skipFirst ? state.entries.length - 1 : state.entries.length,
    isDirty: head.source === 'user' && head.value.trim().length > 0,
  };
}
